import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  Avatar,
  Chip,
  Divider,
  Stack,
  IconButton,
} from '@mui/material';
import PetsIcon from '@mui/icons-material/Pets';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import api from '../api/api';

// ---------------------------------------------------------------------------
// Este componente es una versión SIMPLIFICADA de ProgramarConsulta.js.
// A propósito NO permite crear un paciente/cliente nuevo: solo sirve para
// buscar un paciente ya existente (con suficientes datos de referencia para
// identificarlo con seguridad) y programar/registrar su consulta.
// Si no se encuentra el paciente, se debe crear primero desde el módulo
// de Clientes/Pacientes.
// ---------------------------------------------------------------------------

const decodeJwtToken = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    const decodedPayload = atob(payloadBase64);
    return JSON.parse(decodedPayload);
  } catch (e) {
    console.error('Error al decodificar el token:', e);
    return null;
  }
};

// Banderas de alerta que ayudan a no agendar consulta al paciente equivocado
const ALERTAS_PACIENTE = [
  { key: 'es_fallecido', label: 'Fallecido', color: 'error' },
  { key: 'es_extraviado', label: 'Extraviado', color: 'warning' },
  { key: 'es_exportado', label: 'Exportado', color: 'default' },
  { key: 'es_cambio_propietario', label: 'Cambió de dueño', color: 'info' },
];

const getAlertasActivas = (paciente) =>
  ALERTAS_PACIENTE.filter((a) => paciente && paciente[a.key]);

// Tarjeta de cada resultado dentro del desplegable de búsqueda
const OpcionPaciente = (props, paciente) => {
  const alertas = getAlertasActivas(paciente);
  return (
    <Box component="li" {...props} key={paciente.id_paciente}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
        <Avatar sx={{ bgcolor: alertas.length ? 'grey.400' : 'primary.light', width: 36, height: 36 }}>
          <PetsIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {paciente.nombre_paciente || 'Sin nombre'}
            {paciente.nombre_especie ? ` · ${paciente.nombre_especie}` : ''}
            {paciente.nombre_raza ? ` (${paciente.nombre_raza})` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            Dueño: {paciente.nombre_cliente || 'N/D'}
            {paciente.telefonos_cliente ? ` · ${paciente.telefonos_cliente}` : ''}
          </Typography>
          {paciente.nombre_sucursal && (
            <Typography variant="caption" color="text.disabled">
              Sucursal: {paciente.nombre_sucursal}
            </Typography>
          )}
        </Box>
        {alertas.length > 0 && (
          <Stack direction="row" spacing={0.5}>
            {alertas.map((a) => (
              <Chip key={a.key} label={a.label} size="small" color={a.color} variant="outlined" />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const BuscarPacienteConsulta = ({ visible, onClose, initialDate, id_paciente }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);

  const [pacientes, setPacientes] = useState([]);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [formData, setFormData] = useState({
    razon_consulta: '',
    fecha_cita: initialDate ? dayjs(initialDate) : dayjs(),
  });

  const [idUsuario, setIdUsuario] = useState(null);
  const [idSucursal, setIdSucursal] = useState(null);

  // Fecha por defecto cada vez que se abre el modal
  useEffect(() => {
    if (visible) {
      setFormData((prev) => ({
        ...prev,
        fecha_cita: initialDate ? dayjs(initialDate) : dayjs(),
      }));
    }
  }, [visible, initialDate]);

  // Datos del usuario autenticado (para armar el payload de la consulta)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = decodeJwtToken(token);
      if (decodedToken) {
        setIdUsuario(decodedToken.id_usuario);
        setIdSucursal(decodedToken.id_sucursal);
      } else {
        setError('Error de autenticación. Por favor, vuelva a iniciar sesión.');
      }
    }
  }, []);

  // Precarga de paciente si se recibe por props o por la URL
  useEffect(() => {
    const fetchAndSetPatient = async (patientId) => {
      try {
        setLoading(true);
        const response = await api.get(`/pacientes/${patientId}`);
        if (response.data) {
          setSelectedPaciente(response.data);
          setError(null);
        } else {
          setError('No se encontró el paciente con el ID proporcionado.');
        }
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError('Error al cargar los datos del paciente.');
      } finally {
        setLoading(false);
      }
    };

    const fetchClientPatients = async (clientId) => {
      try {
        setLoading(true);
        const response = await api.get(`/pacientes/por-cliente/${clientId}`);
        if (response.data && response.data.length > 0) {
          setSelectedPaciente(response.data[0]);
          setError(null);
        } else {
          setInfoMsg(
            'Este cliente no tiene pacientes registrados. Crea el paciente desde el módulo de Clientes antes de programar la consulta.'
          );
        }
      } catch (err) {
        console.error('Error fetching patient data for client:', err);
        setError('Error al cargar los pacientes del cliente.');
      } finally {
        setLoading(false);
      }
    };

    if (id_paciente) {
      fetchAndSetPatient(id_paciente);
    } else if (id) {
      fetchClientPatients(id);
    }
  }, [id, id_paciente]);

  // Búsqueda con debounce contra /pacientes/buscar (Sincronizado a 2 caracteres como en BuscarPacienteModal)
  useEffect(() => {
    const timerId = setTimeout(async () => {
      if (searchQuery && searchQuery.trim().length >= 2) {
        setLoadingSearch(true);
        try {
          const response = await api.get('/pacientes/buscar', {
            params: { terminos_busqueda: searchQuery }
          });
          const pacientesConKeys = response.data.map((paciente) => ({
            ...paciente,
            key: paciente.id_paciente,
          }));
          setPacientes(pacientesConKeys);
        } catch (err) {
          console.error('Error fetching patients:', err);
          setError('No se pudieron cargar los pacientes.');
          setPacientes([]);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setPacientes([]);
      }
    }, 400); // 400ms de debounce
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  const handleProgramarConsulta = async (isExpress = false) => {
    if (!selectedPaciente) {
      setError('Selecciona un paciente antes de continuar.');
      return;
    }
    if (!formData.razon_consulta) {
      setError('Por favor, completa la razón de la consulta.');
      return;
    }
    if (!isExpress && !formData.fecha_cita) {
      setError('Por favor, completa la fecha de la cita.');
      return;
    }
    if (!idUsuario || !idSucursal) {
      setError('Error: No se pudo obtener la información de usuario y sucursal del token.');
      return;
    }

    try {
      setLoading(true);

      const citaPayload = {
        id_paciente: selectedPaciente.id_paciente,
        razon_consulta: formData.razon_consulta,
        fecha_cita: isExpress
          ? new Date().toISOString()
          : formData.fecha_cita
          ? formData.fecha_cita.format('YYYY-MM-DDTHH:mm:ss')
          : null,
        id_usuario: idUsuario,
        id_sucursal: idSucursal,
        es_express: isExpress,
        es_pendiente: !isExpress,
      };

      await api.post('/consultas/programar', citaPayload);
      setError(null);
      alert('Consulta programada exitosamente');
      onClose();
    } catch (error) {
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail.map(
          (err) => `${err.loc.join(' -> ')}: ${err.msg}`
        );
        setError(`Error(es) de validación:\n${errorMessages.join('\n')}`);
      } else {
        setError('Error al programar consulta: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!visible) {
    return null;
  }

  const alertasSeleccionado = getAlertasActivas(selectedPaciente);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 560,
          mx: 'auto',
        }}
      >
        {/* Encabezado */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <PetsIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Programar Consulta
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Busca un paciente existente para agendar su consulta
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Buscador de pacientes */}
          <Autocomplete
            options={pacientes}
            filterOptions={(x) => x} // <--- IMPORTANTE: Desactiva el filtro local de MUI para mostrar todos los resultados que trae la API
            getOptionKey={(option) => option.id_paciente}
            getOptionLabel={(option) =>
              option ? `${option.nombre_paciente ?? ''} - ${option.nombre_cliente ?? ''}` : ''
                      }
                      isOptionEqualToValue={(option, value) => option.id_paciente === value.id_paciente}
                      loading={loadingSearch}
                      value={selectedPaciente}
                      noOptionsText={
              searchQuery.trim().length > 0 && searchQuery.trim().length < 2
                ? 'Escribe al menos 2 caracteres…'
                : 'Sin resultados. Verifica el nombre, dueño, teléfono o código de mascota (PAC-XXXX).'
            }
            onChange={(event, newValue) => {
              if (!id_paciente) {
                setSelectedPaciente(newValue);
                setInfoMsg(null);
              }
            }}
            onInputChange={(event, newInputValue) => {
              if (!id_paciente) {
                setSearchQuery(newInputValue);
              }
            }}
            readOnly={!!id_paciente}
            renderOption={(props, option) => OpcionPaciente(props, option)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar paciente por nombre, dueño, teléfono o código"
                placeholder="Ej: Firulais, Juan Pérez, 7777-8888…"
                fullWidth
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 1 }} />,
                  endAdornment: (
                    <React.Fragment>
                      {loadingSearch ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />

          {/* Tarjeta de identidad del paciente seleccionado, con datos de referencia
              para confirmar que es el paciente correcto antes de agendar */}
          {selectedPaciente && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alertasSeleccionado.length ? 'warning.main' : 'success.main',
                backgroundColor: alertasSeleccionado.length ? 'warning.50' : 'success.50',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Avatar sx={{ bgcolor: alertasSeleccionado.length ? 'warning.main' : 'success.main' }}>
                  {alertasSeleccionado.length ? <PetsIcon /> : <CheckCircleIcon />}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {selectedPaciente.nombre_paciente}
                    {selectedPaciente.nombre_especie ? ` · ${selectedPaciente.nombre_especie}` : ''}
                    {selectedPaciente.nombre_raza ? ` (${selectedPaciente.nombre_raza})` : ''}
                  </Typography>

                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Dueño: <strong>{selectedPaciente.nombre_cliente || 'N/D'}</strong>
                    </Typography>
                    {selectedPaciente.telefonos_cliente && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 15 }} /> {selectedPaciente.telefonos_cliente}
                      </Typography>
                    )}
                    {selectedPaciente.nombre_sucursal && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StorefrontIcon sx={{ fontSize: 15 }} /> {selectedPaciente.nombre_sucursal}
                      </Typography>
                    )}
                  </Stack>

                  {alertasSeleccionado.length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                      {alertasSeleccionado.map((a) => (
                        <Chip key={a.key} label={a.label} size="small" color={a.color} />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>

              {alertasSeleccionado.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Este paciente tiene alertas activas. Confirma que es el paciente correcto antes de
                  continuar.
                </Alert>
              )}
            </Paper>
          )}

          {infoMsg && !selectedPaciente && <Alert severity="info">{infoMsg}</Alert>}

          <Divider />

          <TextField
            label="Razón de Consulta"
            fullWidth
            multiline
            minRows={2}
            value={formData.razon_consulta}
            onChange={(e) => setFormData({ ...formData, razon_consulta: e.target.value })}
          />

          <DateTimePicker
            label="Fecha de Cita"
            value={formData.fecha_cita}
            onChange={(newValue) => setFormData({ ...formData, fecha_cita: newValue })}
            format="DD/MM/YYYY hh:mm A"
            ampm={true}
            minutesStep={30}
            slotProps={{
              textField: { fullWidth: true },
              timeClock: { minutesStep: 30 },
            }}
          />

          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>{error}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onClose} variant="outlined">
              Cancelar
            </Button>

            {selectedPaciente && (
              <Button
                onClick={() => handleProgramarConsulta(true)}
                variant="contained"
                disabled={loading || !formData.razon_consulta}
                sx={{ backgroundColor: 'orange', '&:hover': { backgroundColor: 'darkorange' } }}
              >
                {loading ? <CircularProgress size={24} /> : 'Consulta Rápida'}
              </Button>
            )}

            <Button
              onClick={() => handleProgramarConsulta()}
              variant="contained"
              disabled={loading || !selectedPaciente || !formData.razon_consulta || !formData.fecha_cita}
            >
              {loading ? <CircularProgress size={24} /> : 'Programar Cita'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default BuscarPacienteConsulta;
